import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, Fragment as _Fragment, renderList as _renderList, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-24ff2403"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["data-depth"]
const _hoisted_2 = {
  key: 0,
  class: "tree-indent relative flex-shrink-0 self-stretch"
}
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", {
  class: "tree-guide",
  "aria-hidden": "true"
}, null, -1))
const _hoisted_4 = [
  _hoisted_3
]
const _hoisted_5 = {
  xmlns: "http://www.w3.org/2000/svg",
  class: "h-[18px] w-[18px] flex-shrink-0 text-gray-400",
  viewBox: "0 0 24 24",
  "aria-hidden": "true"
}
const _hoisted_6 = ["d"]
const _hoisted_7 = { class: "text-left text-[13px] text-gray-400" }
const _hoisted_8 = ["data-depth", "data-node-type", "data-node-id", "aria-level", "aria-expanded"]
const _hoisted_9 = {
  key: 0,
  class: "tree-indent relative flex-shrink-0 self-stretch"
}
const _hoisted_10 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", {
  class: "tree-guide",
  "aria-hidden": "true"
}, null, -1))
const _hoisted_11 = [
  _hoisted_10
]
const _hoisted_12 = ["d"]
const _hoisted_13 = {
  key: 2,
  class: "w-6 flex-shrink-0",
  "aria-hidden": "true"
}
const _hoisted_14 = {
  xmlns: "http://www.w3.org/2000/svg",
  class: "h-[19px] w-[19px]",
  viewBox: "0 0 24 24",
  "aria-hidden": "true"
}
const _hoisted_15 = ["d"]
const _hoisted_16 = { class: "min-w-0 flex-1 text-left" }
const _hoisted_17 = {
  "data-testid": "tree-row-meta",
  class: "m-0 flex items-center gap-1 text-xs text-gray-400"
}
const _hoisted_18 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { "aria-hidden": "true" }, "·", -1))
const _hoisted_19 = { class: "font-mono text-[11px]" }
const _hoisted_20 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { "aria-hidden": "true" }, "·", -1))
const _hoisted_21 = { class: "truncate" }
const _hoisted_22 = {
  class: "whitespace-nowrap rounded-md bg-gray-100 px-2.5 py-1 text-[13px] text-gray-700",
  "data-testid": "tree-row-relation-chip"
}
const _hoisted_23 = { key: 0 }
const _hoisted_24 = {
  key: 3,
  class: "flex min-w-[124px] flex-shrink-0 items-center justify-end gap-1"
}
const _hoisted_25 = {
  key: 0,
  "data-testid": "tree-row-origin-overflow",
  class: "inline-flex items-center whitespace-nowrap rounded-md border border-purple-200 bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700"
}

import { computed } from 'vue'
import {
  mdiCalendarMonth,
  mdiCalendarRemove,
  mdiChevronRight,
  mdiDomain,
  mdiHospitalBuilding
} from '@mdi/js'

import { ResourceType } from '@/Network/Types/Responses/PermissionResponses'
import { getRelationLabel, getResourceTypeLabel } from '@/constants/authorizationLabels'

import type { FlatTreeRow } from '../flattenTree'
import { getOriginName, getOriginTooltip } from '../utils'
import PermissionOriginBadge from './PermissionOriginBadge.vue'

/** Badges de origem visíveis por row; o excedente vira o chip "+N" com tooltip. */
const MAX_VISIBLE_ORIGIN_BADGES = 2


export default /*#__PURE__*/_defineComponent({
  __name: 'PermissionTreeRow',
  props: {
    row: {}
  },
  emits: ["toggle"],
  setup(__props: any, { emit }) {

const props = __props;





const node = computed(() => props.row.node)

const typeLabel = computed(() => getResourceTypeLabel(node.value?.resource_type))

const TYPE_STYLES: Partial<Record<ResourceType, { icon: string; container: string }>> = {
  [ResourceType.Company]: { icon: mdiDomain, container: 'bg-primary5 text-primary30' },
  [ResourceType.HealthUnit]: { icon: mdiHospitalBuilding, container: 'bg-gray-100 text-gray-700' },
  [ResourceType.Schedule]: { icon: mdiCalendarMonth, container: 'bg-gray-50 text-gray-500' }
}

const typeStyle = computed(
  () =>
    TYPE_STYLES[node.value?.resource_type ?? ResourceType.Company] ?? {
      icon: mdiDomain,
      container: 'bg-gray-100 text-gray-700'
    }
)

const nameClass = computed(() =>
  node.value?.resource_type === ResourceType.Schedule
    ? 'font-medium text-gray-700'
    : 'font-semibold text-gray-800'
)

const nameTooltip = computed(() => {
  const current = node.value
  if (!current) return ''
  return `${current.resource_name} · ${typeLabel.value} · ID ${current.resource_id}`
})

/** Origens do nó na ordem do backend (direct primeiro, depois grupos por nome). */
const origins = computed(() => props.row.origins ?? [])

const visibleOrigins = computed(() => origins.value.slice(0, MAX_VISIBLE_ORIGIN_BADGES))

const overflowOrigins = computed(() => origins.value.slice(MAX_VISIBLE_ORIGIN_BADGES))

/** Origem(ns) que concedem a relation efetiva (2ª linha do tooltip do chip de papel). */
const effectiveGrantors = computed(() => {
  const current = node.value
  if (!current) return ''
  return origins.value
    .filter((origin) => origin.relation === current.effective_relation)
    .map(getOriginName)
    .join(', ')
})

/** Texto da linha vazia varia conforme o nível do pai (empresa sem unidades × unidade sem escalas). */
const emptyText = computed(() => {
  if (props.row.parentType === ResourceType.Company) {
    return 'Nenhuma unidade com permissão nesta empresa'
  }
  return 'Nenhuma escala com permissão nesta unidade'
})

/** Resumo da meta: empresa → nº de unidades; unidade → nº de escalas; escala → nada. */
const summary = computed(() => {
  const current = node.value
  if (!current) return ''
  const childCount = current.children?.length ?? 0
  if (current.resource_type === ResourceType.Company) {
    return childCount === 1 ? '1 unidade' : `${childCount} unidades`
  }
  if (current.resource_type === ResourceType.HealthUnit) {
    if (childCount === 0) return 'sem escalas'
    return childCount === 1 ? '1 escala' : `${childCount} escalas`
  }
  return ''
})

const handleToggle = (): void => {
  if (!props.row.hasChildren) return
  emit('toggle', props.row.key)
}

return (_ctx: any,_cache: any) => {
  const _component_v_tooltip = _resolveComponent("v-tooltip")!

  return (_ctx.row.kind === 'empty')
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        "data-testid": "permission-tree-empty-row",
        "data-depth": _ctx.row.depth,
        class: "tree-row flex min-h-[44px] items-center gap-2.5 px-2",
        style: _normalizeStyle({ '--tree-depth': _ctx.row.depth })
      }, [
        (_ctx.row.depth > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, _hoisted_4))
          : _createCommentVNode("", true),
        (_openBlock(), _createElementBlock("svg", _hoisted_5, [
          _createElementVNode("path", {
            d: _unref(mdiCalendarRemove),
            fill: "currentColor"
          }, null, 8, _hoisted_6)
        ])),
        _createElementVNode("span", _hoisted_7, _toDisplayString(emptyText.value), 1)
      ], 12, _hoisted_1))
    : (node.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          role: "treeitem",
          "data-testid": "permission-tree-node",
          "data-depth": _ctx.row.depth,
          "data-node-type": node.value.resource_type,
          "data-node-id": node.value.resource_id,
          "aria-level": _ctx.row.depth + 1,
          "aria-expanded": _ctx.row.hasChildren ? String(Boolean(_ctx.row.open)) : undefined,
          class: _normalizeClass(["tree-row flex min-h-[56px] items-center gap-2.5 rounded-lg px-2 hover:bg-gray-50", [_ctx.row.hasChildren ? 'cursor-pointer' : '', _ctx.row.dimmed ? 'opacity-50' : '']]),
          style: _normalizeStyle({ '--tree-depth': _ctx.row.depth }),
          onClick: handleToggle
        }, [
          (_ctx.row.depth > 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, _hoisted_11))
            : _createCommentVNode("", true),
          (_ctx.row.hasChildren)
            ? (_openBlock(), _createElementBlock("svg", {
                key: 1,
                "data-testid": "tree-row-chevron",
                xmlns: "http://www.w3.org/2000/svg",
                class: _normalizeClass(["h-[22px] w-[22px] flex-shrink-0 text-gray-500 transition-transform", _ctx.row.open ? 'rotate-90' : '']),
                viewBox: "0 0 24 24",
                "aria-hidden": "true"
              }, [
                _createElementVNode("path", {
                  d: _unref(mdiChevronRight),
                  fill: "currentColor"
                }, null, 8, _hoisted_12)
              ], 2))
            : (_openBlock(), _createElementBlock("span", _hoisted_13)),
          _createElementVNode("div", {
            class: _normalizeClass(["flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-lg", typeStyle.value.container])
          }, [
            (_openBlock(), _createElementBlock("svg", _hoisted_14, [
              _createElementVNode("path", {
                d: typeStyle.value.icon,
                fill: "currentColor"
              }, null, 8, _hoisted_15)
            ]))
          ], 2),
          _createElementVNode("div", _hoisted_16, [
            _createElementVNode("p", {
              class: _normalizeClass(["m-0 truncate text-left text-sm", nameClass.value])
            }, [
              _createTextVNode(_toDisplayString(node.value.resource_name) + " ", 1),
              _createVNode(_component_v_tooltip, {
                activator: "parent",
                location: "top"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(nameTooltip.value), 1)
                ]),
                _: 1
              })
            ], 2),
            _createElementVNode("p", _hoisted_17, [
              _createElementVNode("span", null, _toDisplayString(typeLabel.value), 1),
              _hoisted_18,
              _createElementVNode("span", _hoisted_19, "ID " + _toDisplayString(node.value.resource_id), 1),
              (summary.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _hoisted_20,
                    _createElementVNode("span", _hoisted_21, _toDisplayString(summary.value), 1)
                  ], 64))
                : _createCommentVNode("", true)
            ])
          ]),
          _createElementVNode("span", _hoisted_22, [
            _createTextVNode(_toDisplayString(_unref(getRelationLabel)(node.value.effective_relation)) + " ", 1),
            _createVNode(_component_v_tooltip, {
              activator: "parent",
              location: "top"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", null, "Efetiva: " + _toDisplayString(_unref(getRelationLabel)(node.value.effective_relation)), 1),
                (effectiveGrantors.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_23, "Concedida por: " + _toDisplayString(effectiveGrantors.value), 1))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ]),
          (origins.value.length > 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleOrigins.value, (origin, index) => {
                  return (_openBlock(), _createBlock(PermissionOriginBadge, {
                    key: `${_ctx.row.key}-origin-${index}`,
                    origin: origin
                  }, null, 8, ["origin"]))
                }), 128)),
                (overflowOrigins.value.length > 0)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_25, [
                      _createTextVNode(" +" + _toDisplayString(overflowOrigins.value.length) + " ", 1),
                      _createVNode(_component_v_tooltip, {
                        activator: "parent",
                        location: "top"
                      }, {
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(overflowOrigins.value, (origin, index) => {
                            return (_openBlock(), _createElementBlock("div", {
                              key: `${_ctx.row.key}-overflow-${index}`
                            }, _toDisplayString(_unref(getOriginTooltip)(origin)), 1))
                          }), 128))
                        ]),
                        _: 1
                      })
                    ]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
        ], 14, _hoisted_8))
      : _createCommentVNode("", true)
}
}

})